// Customers' Equipment (Blueprint only; pages/ux-full customers/equipment): every customer's equipment
// in one list, drawn with ui-web's RowList (equipmentRows) over equipment.list, a page at a time.
// Everything, the services due in the next 30 days, or the warranties ending in the next 60, by kind when
// one is picked; the services due soonest beside it. A row opens the customer's record on that piece. Who opens it is
// who opens a customer's record (blueprint/places.ts), as customers.view reaches.
// On ui-web's ListPage pattern (patterns/README.md, BL-189): its title block, the views and the kind, the
// list and Service due soon beside it, each region saying its own loading, empty and failed.
import { useEffect, useState } from 'react';
import type { EquipmentRow } from '@flowology/ui-web';
import { equipmentRows, RowList } from '@flowology/ui-web/fl';
import { ListPage, ListPick, type ListStatus } from '@flowology/ui-web/patterns';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { todayIn } from '../books/model.ts';
import { usePaged } from '../blueprint/paged.ts';
import {
  byDue,
  DUE_SIDE_INPUT,
  EQUIPMENT_SHOWS,
  equipmentInput,
  equipmentListProps,
  kindLabel,
  listCount,
  onRecord,
  type EquipmentPage,
  type EquipmentShow,
} from './equipment-model.ts';

const rowsOf = (a: EquipmentPage) => a.equipment;
/** A region's state from what's read: rows (or none yet) and the server's refusal. */
const stateOf = (rows: readonly unknown[] | null, error: string | null): ListStatus =>
  rows ? (rows.length ? 'ready' : 'empty') : error ? 'failed' : 'loading';

export function EquipmentScreen({
  nav,
  timeZone,
  onOpen,
}: {
  nav: NavProps;
  timeZone: string;
  /** Opens the customer's record on this piece. */
  onOpen: (e: EquipmentRow) => void;
}) {
  const [show, setShow] = useState<EquipmentShow>('all');
  const [kind, setKind] = useState<string | null>(null);
  const list = usePaged('equipment.list', equipmentInput(show, kind), rowsOf);
  const due = usePaged('equipment.list', DUE_SIDE_INPUT, rowsOf);
  // the kinds on record, from the first page read; they don't change with the view
  const [kinds, setKinds] = useState<EquipmentPage['kinds']>(null);
  useEffect(() => {
    if (list.first?.kinds && !kind) setKinds(list.first.kinds);
  }, [list.first]);
  const today = todayIn(timeZone);
  const here = EQUIPMENT_SHOWS.find((s) => s.show === show)!;
  const total = onRecord(kinds);
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <ListPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: 'Equipment we look after',
            lede: `${total === null ? 'Every customer’s equipment' : `${total} ${total === 1 ? 'piece' : 'pieces'} on record`}: how old each is, its warranty, the last visit and what’s due. Open one to see it on the customer’s record.`,
          }}
          views={{
            label: 'Show',
            items: EQUIPMENT_SHOWS.map((s) => ({ id: s.show, label: s.label })),
            value: show,
            onChange: (id) => setShow(id as EquipmentShow),
          }}
          {...(kinds && kinds.length > 1
            ? {
                filters: (
                  <ListPick
                    label="Kind"
                    value={kind ?? ''}
                    options={[
                      { value: '', label: 'Every kind' },
                      ...kinds.map((k) => ({ value: k.kind, label: kindLabel(k) })),
                    ]}
                    onChange={(v) => setKind(v || null)}
                  />
                ),
              }
            : {})}
          list={{
            title: kind ? `${here.label}: ${kind}` : here.label,
            ...(list.rows ? { count: listCount(list.first, list.rows) } : {}),
            status: stateOf(list.rows, list.error),
            emptyText: here.empty,
            failedText: list.error ?? 'The equipment didn’t load.',
            what: 'the equipment',
            onRetry: list.reload,
            children: list.rows && (
              <RowList
                key={`${show}-${kind ?? ''}`}
                label="Equipment"
                rows={equipmentRows(equipmentListProps(today, list.rows), { onOpen })}
                first={5}
                {...(list.more ? { onMore: list.more } : {})}
              />
            ),
          }}
          aside={{
            title: 'Service due soon',
            ...(due.rows ? { count: listCount(due.first, due.rows) } : {}),
            status: stateOf(due.rows, due.error),
            emptyText: 'No service is due in the next 30 days.',
            failedText: due.error ?? 'What’s due didn’t load.',
            what: 'what’s due',
            onRetry: due.reload,
            children: due.rows && (
              <RowList
                label="Equipment"
                rows={equipmentRows(equipmentListProps(today, byDue(due.rows)), { onOpen, brief: true })}
                first={5}
                {...(due.more ? { onMore: due.more } : {})}
              />
            ),
          }}
        />
      </main>
    </div>
  );
}
